import { createStore, useStore } from 'zustand';

import type { ComposerAccess } from '../model/composer';

type ComposerBridges = { bridges: Record<string, ComposerAccess> };

const composerBridgeStore = createStore<ComposerBridges>(() => ({ bridges: {} }));

/**
 * Lends a conversation's composer to its reply draft, and returns how to take
 * it back.
 *
 * On a phone the draft is a full-screen route over the chat, so it cannot be
 * handed the composer as props. The chat stays mounted beneath it and lends
 * the composer here instead. Taking it back only clears the slot if it still
 * holds this loan, so a chat remounting for the same conversation cannot lose
 * the newer one.
 */
export function lendComposer(conversationId: string, access: ComposerAccess): () => void {
  composerBridgeStore.setState((state) => ({
    bridges: { ...state.bridges, [conversationId]: access },
  }));

  return () => {
    composerBridgeStore.setState((state) => {
      if (state.bridges[conversationId] !== access) {
        return state;
      }

      const bridges = { ...state.bridges };

      delete bridges[conversationId];

      return { bridges };
    });
  };
}

/** The composer lent for a conversation, or null when no chat for it is open. */
export function useLentComposer(conversationId: string): ComposerAccess | null {
  return useStore(composerBridgeStore, (state) => state.bridges[conversationId] ?? null);
}
